/**
 * Inline help for the patient-portal guarantor lookup, for St. Luke's dual-EHR period.
 *
 * <h3>The problem</h3>
 * The lookup matches on the GUARANTOR identifier — a Meditech guarantor number, or a Cerner guarantor
 * CMRN. A Cerner statement also prints per-encounter FIN numbers, which are far more prominent, so
 * typing a FIN is a likely first attempt. It returns the same "No accounts found" as a wrong surname,
 * leaving the patient with no way to work out what went wrong.
 *
 * <h3>The rule, and why it is LENGTH and not prefix</h3>
 * Measured, not assumed:
 *   - Guarantor CMRN: 28 distinct values across the RON_MTSTMT_* sample statements (STMT field 24,
 *     "Guarantor C MRN" in the ProFit layout) are all 7 or 8 digits. The payments extract also carries
 *     6-digit values, so the accepted range is 6-8.
 *   - Encounter FIN: 9 or 11 digits, 880-prefix on 5,879 of 5,879 rows of the 2026-09-09 extract.
 *
 * A PREFIX rule would be wrong. The sample statement extract contains guarantor CMRNs whose leading
 * digits are 88, 89 and 90, so a "starts with 88/89" rule would flag genuine guarantor numbers as
 * mistakes. The two LENGTH sets do not overlap, so length alone separates them, and
 * guarantorLookupHint.test.ts asserts that across every leading pair rather than against a list of
 * identifiers.
 *
 * <h3>Advisory only</h3>
 * This never blocks submission. The shapes come from the data we have, not from a guarantee, and a
 * patient who cannot pay because we guessed their number's shape wrong is a far worse outcome than a
 * patient who sees an unnecessary hint. Meditech guarantor numbers are not digits-only (they carry a
 * letter prefix such as GN), so they never reach this check.
 */

/** Digits-only lengths a Cerner guarantor CMRN is known to take. */
const CMRN_LENGTHS = [6, 7, 8];

/** Digits-only lengths an encounter FIN is known to take. */
const FIN_LENGTHS = [9, 11];

/**
 * Does this look like an encounter/FIN typed into the guarantor field?
 * Only true for a digits-only value of a length a CMRN never has and a FIN does.
 */
export const looksLikeEncounterFin = (value?: string | null): boolean => {
  const v = (value || '').trim();
  if (!/^\d+$/.test(v)) return false;
  return FIN_LENGTHS.includes(v.length) && !CMRN_LENGTHS.includes(v.length);
};

/**
 * Advisory hint for the guarantor field, or null when there is nothing useful to say.
 * Deliberately returns null for an empty field, a Meditech-style value, and a plausible CMRN.
 */
export const guarantorLookupHint = (value?: string | null): string | null =>
  looksLikeEncounterFin(value)
    ? 'That looks like a visit or encounter number. Please enter your guarantor number or CMRN '
      + 'instead — it is the 6 to 8 digit number on your statement.'
    : null;
